import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import { useCurrentEditor } from "@tiptap/react";
import {
  BoldIcon,
  Braces,
  Heading1,
  Heading2,
  Heading3,
  ItalicIcon,
  UnderlineIcon,
} from "lucide-react";

import { cn } from "../utils";

import i18n from "@/i18n";
import { PlaceholderSuggestionList } from "./extensions/placeholder";
import useDialogModal from "@/hooks/useDialogModal";

const { t } = i18n;

const ToggleButton = ({ isActive, ...props }) => {
  return (
    <Button
      variant="ghost"
      {...props}
      className={cn("hover:bg-slate-100", {
        "bg-slate-200 hover:bg-slate-200": isActive,
      })}
    />
  );
};

export const TiptapEditorToolbar = ({ readOnly }) => {
  const { editor } = useCurrentEditor();
  const { prompt, dialogElement } = useDialogModal();

  const [placeholderFields] = useState(() => [
    {
      id: "calculation",
      type: "calculation",
      description: t("email_editor.placeholders.calculation.description"),
    },
    ...(editor?.commands?.getPlaceholderFields?.() ?? []),
  ]);

  return (
    <div className="flex gap-2 rounded-t-lg border-b border-slate-200 bg-background p-2">
      <ToggleButton
        title={t("email_editor.toolbar.h1")}
        disabled={readOnly}
        isActive={editor.isActive("heading", { level: 1 })}
        onClick={() => {
          editor.chain().focus().toggleHeading({ level: 1 }).run();
        }}
      >
        <Heading1 />
      </ToggleButton>
      <ToggleButton
        title={t("email_editor.toolbar.h2")}
        disabled={readOnly}
        isActive={editor.isActive("heading", { level: 2 })}
        onClick={() => {
          editor.chain().focus().toggleHeading({ level: 2 }).run();
        }}
      >
        <Heading2 />
      </ToggleButton>
      <ToggleButton
        title={t("email_editor.toolbar.h3")}
        disabled={readOnly}
        isActive={editor.isActive("heading", { level: 3 })}
        onClick={() => {
          editor.chain().focus().toggleHeading({ level: 3 }).run();
        }}
      >
        <Heading3 />
      </ToggleButton>
      <ToggleButton
        title={t("email_editor.toolbar.bold")}
        disabled={readOnly}
        isActive={editor.isActive("bold")}
        onClick={() => {
          editor.chain().focus().toggleBold().run();
        }}
      >
        <BoldIcon />
      </ToggleButton>
      <ToggleButton
        title={t("email_editor.toolbar.italic")}
        disabled={readOnly}
        isActive={editor.isActive("italic")}
        onClick={() => {
          editor.chain().focus().toggleItalic().run();
        }}
      >
        <ItalicIcon />
      </ToggleButton>
      <ToggleButton
        title={t("email_editor.toolbar.underline")}
        disabled={readOnly}
        isActive={editor.isActive("underline")}
        onClick={() => {
          editor.chain().focus().toggleUnderline().run();
        }}
      >
        <UnderlineIcon />
      </ToggleButton>

      <div>
        <Button
          title={t("email_editor.toolbar.insert_placeholder")}
          variant="ghost"
          disabled={readOnly}
          onClick={() =>
            prompt(
              t("email_editor.toolbar.insert_placeholder"),
              ({ onCancel }) => (
                <PlaceholderSuggestionList
                  items={placeholderFields}
                  command={(item) => {
                    if (item.type === "calculation") {
                      editor
                        .chain()
                        .focus()
                        .insertPlaceholderCalculation(item.id)
                        .run();
                    } else {
                      editor
                        .chain()
                        .focus()
                        .insertPlaceholderField(item.id)
                        .run();
                    }
                    onCancel();
                  }}
                />
              ),
              { submitText: null },
            )
          }
        >
          <Braces />
        </Button>
      </div>
      {dialogElement}
    </div>
  );
};
